import Vue from 'vue';
import agentProfilesService from '@/services/account/agentProfilesService';
import communicationChannelsService from '@/services/account/communicationChannelsService';

/** Channel types a customer talks through; PLATFORM (the lab), ONBOARDING and META are internal. */
export const CUSTOMER_CHANNEL_TYPES = ['WHATSAPP', 'INSTAGRAM', 'FACEBOOK', 'WEBCHAT', 'GMAIL', 'OUTLOOK', 'EMAIL', 'PHONE'];

/**
 * Agent profiles of the account. Not persisted to localStorage (unlike
 * agentSettings): they are cheap to refetch and a stale default would be worse
 * than a short spinner.
 */

const unwrapList = (res) => {
    const data = res?.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.content)) return data.content;
    return [];
};

const state = () => ({
    profiles: [],
    isLoading: false,
    loaded: false,
    /** Customer-facing channels of the account, shared by the profile list, detail and badges. */
    channels: [],
    channelsLoaded: false,
});

const getters = {
    profiles: (state) => state.profiles,
    isLoading: (state) => state.isLoading,
    loaded: (state) => state.loaded,
    byId: (state) => (id) => state.profiles.find((p) => p.uuid === id) || null,
    defaultProfile: (state) => state.profiles.find((p) => p.default_profile) || null,
    /** Default first, the rest in creation order. */
    orderedProfiles: (state) =>
        [...state.profiles].sort((a, b) => {
            if (a.default_profile !== b.default_profile) return a.default_profile ? -1 : 1;
            return (a.date_created || 0) - (b.date_created || 0);
        }),
    enabledProfiles: (state) => state.profiles.filter((p) => p.enabled !== false),
    /** Name of a profile; an empty or unknown id means the default profile. */
    profileName: (state, getters) => (id) => {
        const profile = (id && getters.byId(id)) || getters.defaultProfile;
        return profile?.name || '';
    },
    profileOptions: (state, getters) => getters.orderedProfiles.map((p) => ({ value: p.uuid, label: p.name })),
    /**
     * Agent profiles are a Lixsa-staff-only feature for now (lixsa-agents does
     * not read them yet), so customers never see any profile UI.
     */
    isFeatureVisible: (state, getters, rootState, rootGetters) => !!rootGetters['user/isWalkonLivosAccount'],
    /**
     * Gates every profile control outside the profiles pages (Training picker
     * and filter, the channel badge): hidden with only the default profile and
     * for anyone who is not Lixsa staff.
     */
    hasMultipleProfiles: (state, getters) => getters.isFeatureVisible && state.profiles.length > 1,
    channels: (state) => state.channels,
    channelsLoaded: (state) => state.channelsLoaded,
    /**
     * The profile that answers a channel: its binding, or the default one when
     * unbound or bound to a profile that no longer exists (same rule as the
     * backend's resolveForChannel).
     */
    profileIdForChannel: (state, getters) => (channel) => {
        const bound = channel?.agent_profile_id;
        if (bound && getters.byId(bound)) return bound;
        return getters.defaultProfile?.uuid || null;
    },
    /** Channels answered by a profile (unbound ones count for the default). */
    channelsOfProfile: (state, getters) => (profileId) =>
        state.channels.filter((channel) => getters.profileIdForChannel(channel) === profileId),
};

const actions = {
    async fetchProfiles({ commit, state, getters }, { force = false } = {}) {
        // Customers never see profiles: do not even ask (the backend would lazily create the default).
        if (!getters.isFeatureVisible) return [];
        if (state.loaded && !force) return state.profiles;
        commit('SET_LOADING', true);
        try {
            const res = await agentProfilesService.fetchAgentProfiles();
            const profiles = unwrapList(res);
            commit('SET_PROFILES', profiles);
            return profiles;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async fetchProfile({ commit }, profileId) {
        const res = await agentProfilesService.getAgentProfile(profileId);
        if (res?.data) commit('UPSERT_PROFILE', res.data);
        return res?.data || null;
    },

    async createProfile({ commit }, payload) {
        const res = await agentProfilesService.createAgentProfile(payload);
        if (res?.data) commit('UPSERT_PROFILE', res.data);
        return res?.data || null;
    },

    async updateProfile({ commit }, { profileId, payload }) {
        const res = await agentProfilesService.updateAgentProfile(profileId, payload);
        if (res?.data) commit('UPSERT_PROFILE', res.data);
        return res?.data || null;
    },

    /** Optimistic switch, rolled back if the backend refuses. */
    async toggleProfile({ commit, getters }, { profileId, enabled }) {
        const previous = getters.byId(profileId)?.enabled;
        commit('SET_PROFILE_ENABLED', { profileId, enabled });
        try {
            const res = await agentProfilesService.updateAgentProfile(profileId, { enabled });
            if (res?.data) commit('UPSERT_PROFILE', res.data);
        } catch (error) {
            commit('SET_PROFILE_ENABLED', { profileId, enabled: previous });
            throw error;
        }
    },

    async deleteProfile({ commit }, profileId) {
        await agentProfilesService.deleteAgentProfile(profileId);
        commit('REMOVE_PROFILE', profileId);
    },

    async linkSkill({ commit }, { profileId, skillId, configValues = {}, enabled = true }) {
        const res = await agentProfilesService.linkSkill(profileId, skillId, configValues, enabled);
        if (res?.data) commit('UPSERT_PROFILE', res.data);
        return res?.data || null;
    },

    async unlinkSkill({ commit }, { profileId, skillId }) {
        const res = await agentProfilesService.unlinkSkill(profileId, skillId);
        if (res?.data) commit('UPSERT_PROFILE', res.data);
        return res?.data || null;
    },

    async fetchChannels({ commit, state, getters }, { force = false } = {}) {
        if (!getters.isFeatureVisible) return [];
        if (state.channelsLoaded && !force) return state.channels;
        const res = await communicationChannelsService.fetchCommunicationChannels(0, 100);
        const channels = unwrapList(res).filter((channel) =>
            CUSTOMER_CHANNEL_TYPES.includes((channel.type || '').toUpperCase()),
        );
        commit('SET_CHANNELS', channels);
        return channels;
    },

    /** Binds a channel; the shared channel list is updated in place, then refetched. */
    async assignChannel({ commit, dispatch }, { channelId, profileId }) {
        const res = await agentProfilesService.assignProfileToChannel(channelId, profileId);
        commit('SET_CHANNEL_PROFILE', { channelId, profileId });
        dispatch('fetchChannels', { force: true }).catch(() => {});
        return res;
    },

    async resetChannel({ commit, dispatch }, { channelId }) {
        const res = await agentProfilesService.resetChannelProfile(channelId);
        commit('SET_CHANNEL_PROFILE', { channelId, profileId: null });
        dispatch('fetchChannels', { force: true }).catch(() => {});
        return res;
    },

    /**
     * Moves a channel to a profile; the default profile means "no binding".
     * @param {{channelId: string, profileId: string|null}} payload
     */
    setChannelProfile({ dispatch, getters }, { channelId, profileId }) {
        const target = profileId && getters.byId(profileId);
        if (!target || target.default_profile) return dispatch('resetChannel', { channelId });
        return dispatch('assignChannel', { channelId, profileId });
    },

    /**
     * Switches the profile between the two skill modes, in one request:
     * - 'inherit': no links, the profile uses every installed skill as is;
     * - 'custom': every installed skill linked (nothing disappears), so the
     *   user narrows by switching off what the profile must not use.
     * @param {{profileId: string, mode: 'inherit'|'custom', skillIds?: string[]}} payload
     */
    setSkillsMode({ dispatch }, { profileId, mode, skillIds = [] }) {
        const skills =
            mode === 'custom' ? skillIds.map((skillId) => ({ skill_id: skillId, enabled: true, config_values: {} })) : [];
        return dispatch('updateProfile', { profileId, payload: { skills } });
    },

    clearAgentProfilesState({ commit }) {
        commit('SET_PROFILES', []);
        commit('SET_LOADED', false);
        commit('SET_CHANNELS', []);
        commit('SET_CHANNELS_LOADED', false);
    },
};

const mutations = {
    SET_PROFILES(state, profiles) {
        state.profiles = profiles;
        state.loaded = true;
    },
    SET_LOADED(state, loaded) {
        state.loaded = loaded;
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    UPSERT_PROFILE(state, profile) {
        const index = state.profiles.findIndex((p) => p.uuid === profile.uuid);
        if (index === -1) state.profiles.push(profile);
        else state.profiles.splice(index, 1, profile);
    },
    REMOVE_PROFILE(state, profileId) {
        state.profiles = state.profiles.filter((p) => p.uuid !== profileId);
    },
    SET_PROFILE_ENABLED(state, { profileId, enabled }) {
        const profile = state.profiles.find((p) => p.uuid === profileId);
        if (profile) Vue.set(profile, 'enabled', enabled);
    },
    SET_CHANNELS(state, channels) {
        state.channels = channels;
        state.channelsLoaded = true;
    },
    SET_CHANNELS_LOADED(state, loaded) {
        state.channelsLoaded = loaded;
    },
    SET_CHANNEL_PROFILE(state, { channelId, profileId }) {
        const channel = state.channels.find((c) => c.uuid === channelId);
        if (channel) Vue.set(channel, 'agent_profile_id', profileId);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
